import { getRouteApi } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import PlusIcon from '@/assets/icons/add.svg?react'
import Button from '@/components/Button/Button'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import GradesTable from '@/components/common/GradesTable'
import useGradesColumns from '@/hooks/useGradesColumns'
import useGroup from '@/queries/useGroup'
import useMarksList from '@/queries/useMarksList'
import useStudentsGrades from '@/queries/useStudentsGrades'
import useAuthStore from '@/store/useAuthStore'

import styles from './GradesView.module.scss'
import AssignmentModal from '../components/AssignmentModal'
import ColumnHeaderGradesView from '../components/ColumnHeaderGradesView'
import GradeSelectCell from '../components/GradeSelectCell'
import { hasEditGroupPermission } from '../utils/permissions'

const routeApi = getRouteApi('/_auth/timetable/$courseId/$groupId/grades')

const GradesView = () => {
  const { t } = useTranslation(['lessonDetails'])

  const navigate = routeApi.useNavigate()
  const { semesterId } = routeApi.useSearch()
  const { groupId, courseId } = routeApi.useParams()

  const user = useAuthStore(store => store.user)

  const {
    data: studentsGrades,
    isLoading,
    refetch
  } = useStudentsGrades({
    groupId,
    semesterId
  })

  const { data: group, isLoading: isGroupLoading } = useGroup(groupId)
  const { data: marks, isFetching: isMarksFetching } = useMarksList()

  const canChangeGroup = useMemo(
    () => hasEditGroupPermission(user, group),
    [user, group]
  )

  const selectedSemester = group?.semesters.find(
    semester => semester.id === semesterId
  )

  const semesterNotArchived = selectedSemester?.status !== 'archived'

  const canChangeGrades = canChangeGroup && semesterNotArchived

  const handleChangeSemester = (semester?: string) => {
    navigate({
      search: {
        semesterId: semester || ''
      }
    })
  }

  const filters: Filter[] = [
    {
      label: t('label.semester'),
      variant: 'semester',
      size: 'wide',
      filterProps: {
        id: 'semester',
        loading: isGroupLoading,
        options: group?.semestersOptions || [],
        value: semesterId,
        onChange: value => handleChangeSemester(value),
        semesterList: group?.semesters ?? []
      }
    }
  ]

  const { columns } = useGradesColumns({
    customGradeColumnHeader: gradeColumn => (
      <ColumnHeaderGradesView
        canChangeGrades={canChangeGrades}
        onGradeColumnChange={refetch}
        tooltip={gradeColumn.name}
        column={gradeColumn}
        semesterId={semesterId}
        courseId={courseId}
        groupId={groupId}
        groupSemesters={group?.semesters || []}
      >
        <div className={styles.gradesColumnName}>{gradeColumn.name}</div>
      </ColumnHeaderGradesView>
    ),
    gradeCell: ({ grade, columnType, cellId }) =>
      canChangeGrades ? (
        <GradeSelectCell
          color={columnType !== 'custom' ? 'dark' : 'light'}
          dataTestId={cellId}
          value={grade.grade}
          gradesOptions={
            (columnType === 'behaviour'
              ? marks?.behaviourMarksOptions
              : marks?.marksOptions) || []
          }
          gradeItemId={grade.id}
          onGradeChange={refetch}
        />
      ) : grade.grade ? (
        marks?.marksMap.get(grade.grade)
      ) : null,
    gradeColumns: studentsGrades?.columns ?? [],
    routeApi
  })

  return (
    <>
      <Filters
        filters={filters}
        hideClearAllButton
        actionsRender={
          canChangeGrades ? (
            <AssignmentModal
              mode="add"
              groupId={groupId}
              onClose={refetch}
              groupSemesters={group?.semesters || []}
              trigger={
                <Button icon={<PlusIcon />}>
                  {t('button.add-assignment')}
                </Button>
              }
            />
          ) : null
        }
      />

      <GradesTable
        withBorders
        noHoverEffect
        isLoading={isLoading || isMarksFetching}
        columns={columns}
        students={studentsGrades?.students ?? []}
      />
    </>
  )
}

export default GradesView
